import { createContext, useCallback, useContext, useState, type ReactNode } from 'react'
import {
  applyTheme,
  defaultTheme,
  lightTheme,
  getStoredThemeName,
  setStoredThemeName,
  type Theme,
} from '@/lib/theme'

interface ThemeContextValue {
  theme: Theme
  isDark: boolean
  toggleTheme: () => void
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = getStoredThemeName()
    return stored === 'Light' ? lightTheme : defaultTheme
  })

  const isDark = theme.name !== 'Light'

  const setTheme = useCallback((next: Theme) => {
    applyTheme(next)
    setStoredThemeName(next.name)
    setThemeState(next)
  }, [])

  const toggleTheme = useCallback(() => {
    setTheme(isDark ? lightTheme : defaultTheme)
  }, [isDark, setTheme])

  return <ThemeContext value={{ theme, isDark, toggleTheme, setTheme }}>{children}</ThemeContext>
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within a ThemeProvider')
  return ctx
}
